import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { Modal } from '../components/Modal';
import { Icon } from '../components/Icons';
import { btnGhost, btnPrimary } from '../components/buttons';
import { adminApi } from '../api/admin';
import { fmtDate } from '../lib/format';
import type { CodeWithCreatorDto } from '../api/types';
import type { ApiError } from '../api/client';

type Filter = 'pending' | 'approved' | 'rejected' | 'all';

export function CodeRequestsScreen() {
  const [filter, setFilter] = useState<Filter>('pending');
  const [rejecting, setRejecting] = useState<CodeWithCreatorDto | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const codes = useQuery({
    queryKey: ['admin', 'codes', filter],
    queryFn: () => adminApi.listCodes(filter === 'all' ? undefined : filter),
  });

  const approve = useMutation({
    mutationFn: (id: number) => adminApi.approveCode(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'codes'] });
      qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setActionError(null);
    },
    onError: (err: ApiError) => setActionError(err?.message ?? 'Approve failed'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => adminApi.rejectCode(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'codes'] });
      qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
      setRejecting(null);
      setRejectReason('');
      setActionError(null);
    },
    onError: (err: ApiError) => setActionError(err?.message ?? 'Reject failed'),
  });

  const rows = codes.data ?? [];

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1280 }}>
      <Card padding={0}>
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', padding: 3, borderRadius: 'var(--r-pill)' }}>
            {(['pending', 'approved', 'rejected', 'all'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--r-pill)',
                  fontSize: 12,
                  fontWeight: 500,
                  background: filter === f ? 'var(--bg-elev)' : 'transparent',
                  color: filter === f ? 'var(--ink)' : 'var(--ink-3)',
                  boxShadow: filter === f ? 'var(--shadow-card)' : 'none',
                  textTransform: 'capitalize',
                }}
              >
                {f}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
            {rows.length} {filter} code{rows.length === 1 ? '' : 's'}
          </div>
        </div>

        {actionError && (
          <div
            style={{
              margin: 16,
              fontSize: 12.5,
              color: 'var(--bad)',
              background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
              border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
              padding: '8px 12px',
              borderRadius: 'var(--r-sm)',
            }}
          >
            {actionError}
          </div>
        )}

        {rows.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            {codes.isLoading ? 'Loading…' : filter === 'pending' ? 'Nothing in the queue 🎉' : 'No codes match this filter.'}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 1, background: 'var(--line)' }}>
            {rows.map((c) => (
              <div key={c.id} style={{ background: 'var(--bg-elev)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontWeight: 700,
                      fontSize: 18,
                      background: c.approval === 'pending' ? 'var(--bg-sunken)' : c.approval === 'approved' ? 'var(--brand-soft)' : 'color-mix(in oklab, var(--bad) 14%, var(--bg-elev))',
                      color: c.approval === 'pending' ? 'var(--ink-2)' : c.approval === 'approved' ? 'var(--brand-ink)' : 'var(--bad)',
                      padding: '5px 12px',
                      borderRadius: 'var(--r-sm)',
                    }}
                  >
                    {c.code}
                  </span>
                  <StatusPill approval={c.approval} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{c.label}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>
                    {c.creator ? (
                      <>by <Link to={`/creators/`} style={{ color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>@{c.creator.handle}</Link> · {c.creator.email}</>
                    ) : (
                      'unknown creator'
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                    {c.kind === 'primary' ? '◆ Primary' : 'Side'} · submitted {fmtDate(c.submittedAt)}
                  </div>
                  {c.rejectionReason && (
                    <div style={{ marginTop: 8, padding: 8, background: 'color-mix(in oklab, var(--bad) 8%, var(--bg-elev))', borderRadius: 'var(--r-sm)', fontSize: 12, color: 'var(--bad)' }}>
                      Reason: {c.rejectionReason}
                    </div>
                  )}
                </div>
                {c.approval === 'pending' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      style={{ ...btnPrimary, flex: 1, justifyContent: 'center' }}
                      onClick={() => approve.mutate(Number(c.id))}
                      disabled={approve.isPending}
                    >
                      <Icon.Check />
                      Approve
                    </button>
                    <button
                      style={{ ...btnGhost, flex: 1, justifyContent: 'center', color: 'var(--bad)', borderColor: 'color-mix(in oklab, var(--bad) 30%, var(--line))' }}
                      onClick={() => {
                        setRejecting(c);
                        setRejectReason('');
                      }}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title={rejecting ? `Reject ${rejecting.code}` : 'Reject code'}
        description="The creator will see this reason on their dashboard."
        footer={
          <>
            <button style={btnGhost} onClick={() => setRejecting(null)}>Cancel</button>
            <button
              style={{ ...btnPrimary, background: 'var(--bad)' }}
              disabled={reject.isPending || rejectReason.length < 2}
              onClick={() => rejecting && reject.mutate({ id: Number(rejecting.id), reason: rejectReason })}
            >
              {reject.isPending ? 'Rejecting…' : 'Reject'}
            </button>
          </>
        }
      >
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. Reserved keyword, trademark conflict, profanity…"
          rows={3}
          maxLength={255}
          style={{
            background: 'var(--bg-elev)',
            border: '1px solid var(--line-strong)',
            borderRadius: 'var(--r-md)',
            padding: '10px 12px',
            fontSize: 14,
            color: 'var(--ink)',
            outline: 'none',
            width: '100%',
            resize: 'vertical',
            fontFamily: 'var(--font-body)',
          }}
        />
        <div style={{ fontSize: 11, color: 'var(--ink-3)', textAlign: 'right' }}>{rejectReason.length} / 255</div>
      </Modal>
    </div>
  );
}

function StatusPill({ approval }: { approval: 'approved' | 'pending' | 'rejected' }) {
  if (approval === 'approved') return <Pill tone="good" size="sm">Approved</Pill>;
  if (approval === 'pending') return <Pill tone="warn" size="sm">Pending review</Pill>;
  return <Pill tone="bad" size="sm">Rejected</Pill>;
}
